import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchGuardEarnings } from "../../services/firestore";
import { watchInvoices } from "../../services/invoices";
import "./GuardLayout.css";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Owner preview of this tab: the business's money in vs money out, not the
// owner's own guard pay. Built from the invoices (the billing record),
// bucketed by the job's scheduled date. Voided invoices don't count.
function OwnerMoneyView({ companyId }) {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState(null);
  const [tab, setTab] = useState("month");
  useEffect(() => {
    if (!companyId) return undefined;
    return watchInvoices(companyId, setInvoices);
  }, [companyId]);

  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const prefix = tab === "month" ? month : String(now.getFullYear());
  const rows = (invoices || []).filter((i) => i.status !== "void" && String(i.scheduledDate || "").startsWith(prefix));
  const billedRows = rows.filter((i) => i.billBroker !== false);
  const billed = billedRows.reduce((s, i) => s + (Number(i.total) || 0), 0);
  const collected = billedRows.filter((i) => i.status === "paid").reduce((s, i) => s + (Number(i.total) || 0), 0);
  const paidOut = rows.reduce((s, i) => s + (Number(i.guardPaidAmount) || 0), 0);
  const awaitingGuardPay = rows.filter((i) => i.payGuard !== false && i.guardPaidAmount == null).length;
  const jobCount = new Set(rows.map((i) => i.jobId)).size;

  const line = (label, value, color, sub) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "13px 16px", borderBottom: "1px solid #f2f2f2" }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#888", marginTop: 1 }}>{sub}</div>}
      </div>
      <div style={{ fontSize: 16, fontWeight: 800, color }}>{value}</div>
    </div>
  );

  return (
    <div style={{ paddingBottom: 24 }}>
      <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #e8e8e8" }}>
        {[{ key: "month", label: "This month" }, { key: "ytd", label: "Year to date" }].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, padding: "14px 0", fontSize: 12, fontWeight: 600, border: "none", background: "none", cursor: "pointer",
            color: tab === t.key ? "#185FA5" : "#888", borderBottom: `2px solid ${tab === t.key ? "#185FA5" : "transparent"}`,
          }}>{t.label}</button>
        ))}
      </div>
      {invoices === null ? (
        <div style={{ padding: "32px 16px", textAlign: "center", color: "#aaa" }}>Loading...</div>
      ) : (
        <>
          <div style={{ background: "#0B1C2E", padding: "18px 20px 20px", textAlign: "center" }}>
            <div style={{ fontSize: 11, color: "#8ab4d4", marginBottom: 4 }}>
              {tab === "month" ? now.toLocaleDateString("en-US", { month: "long", year: "numeric" }) : `${now.getFullYear()} so far`} · money in − money out
            </div>
            <div style={{ fontSize: 34, fontWeight: 700, color: "#fff" }}>{money(collected - paidOut)}</div>
            <div style={{ fontSize: 11, color: "#8ab4d4", marginTop: 4 }}>{jobCount} billed job{jobCount === 1 ? "" : "s"}</div>
          </div>
          <div style={{ background: "#fff", margin: "12px 12px 0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div style={{ padding: "10px 16px 4px", fontSize: 11, fontWeight: 800, color: "#2d7a2d", letterSpacing: 0.5 }}>MONEY IN</div>
            {line("Billed to brokers", money(billed), "#111")}
            {line("Collected", money(collected), "#2d7a2d")}
            {line("Still owed to you", money(billed - collected), "#a36b1d")}
            <div style={{ padding: "12px 16px 4px", fontSize: 11, fontWeight: 800, color: "#a32d2d", letterSpacing: 0.5 }}>MONEY OUT</div>
            {line("Paid to guards", money(paidOut), "#a32d2d", "only pay you've recorded as paid")}
            {line("Guard pay not recorded yet", `${awaitingGuardPay} job${awaitingGuardPay === 1 ? "" : "s"}`, "#666")}
          </div>
          <div style={{ padding: "14px 12px 0" }}>
            <button className="g-btn g-btn-primary" onClick={() => navigate("/owner/financials")}>Open Financials ›</button>
          </div>
          <div style={{ fontSize: 11, color: "#888", textAlign: "center", padding: "10px 16px" }}>
            Guards see their own pay on this tab. Counted by job date; voided invoices left out.
          </div>
        </>
      )}
    </div>
  );
}

export default function GuardEarnings() {
  const { userProfile, isOwner } = useAuth();
  // The owner previewing the guard app sees the business's numbers instead.
  if (isOwner) return <OwnerMoneyView companyId={userProfile?.companyId} />;
  return <GuardOwnEarnings userProfile={userProfile} />;
}

function GuardOwnEarnings({ userProfile }) {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("month");

  useEffect(() => {
    if (!userProfile?.id) return;
    const unsub = watchGuardEarnings(userProfile.id, (data) => {
      setEarnings(data);
      setLoading(false);
    });
    return unsub;
  }, [userProfile]);

  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const currentYear = String(now.getFullYear());
  const priorYear = String(now.getFullYear() - 1);

  const monthEarnings = earnings.filter((e) => e.month === currentMonth);
  const monthTotal = monthEarnings.reduce((s, e) => s + (e.amount || 0), 0);
  const monthJobs = monthEarnings.reduce((s, e) => s + (e.jobCount || 1), 0);
  const monthHours = monthEarnings.reduce((s, e) => s + (e.hours || 0), 0);

  // Group by month for YTD
  const ytdMonths = {};
  earnings.filter((e) => e.month?.startsWith(currentYear)).forEach((e) => {
    if (!ytdMonths[e.month]) ytdMonths[e.month] = { month: e.month, jobs: 0, hours: 0, earned: 0, paid: true };
    ytdMonths[e.month].jobs += e.jobCount || 1;
    ytdMonths[e.month].hours += e.hours || 0;
    ytdMonths[e.month].earned += e.amount || 0;
    if (e.status !== "paid") ytdMonths[e.month].paid = false;
  });
  const ytdRows = Object.values(ytdMonths).sort((a, b) => b.month.localeCompare(a.month));

  // By year
  const yearTotals = {};
  [currentYear, priorYear].forEach((yr) => {
    const rows = earnings.filter((e) => e.month?.startsWith(yr));
    yearTotals[yr] = {
      jobs: rows.reduce((s, e) => s + (e.jobCount || 1), 0),
      hours: rows.reduce((s, e) => s + (e.hours || 0), 0),
      earned: rows.reduce((s, e) => s + (e.amount || 0), 0),
    };
  });

  function fmt(n) { return `$${Number(n || 0).toFixed(2)}`; }
  function fmtMonth(m) {
    const [y, mo] = m.split("-");
    return new Date(parseInt(y), parseInt(mo) - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  }

  return (
    <div style={{ paddingBottom: 24 }}>
      {/* Tab bar */}
      <div style={{ display: "flex", background: "#fff", borderBottom: "1px solid #e8e8e8" }}>
        {[
          { key: "month", label: "This month" },
          { key: "ytd",   label: "Year to date" },
          { key: "year",  label: "By year" },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            flex: 1, padding: "14px 0", fontSize: 12, fontWeight: 600,
            border: "none", background: "none", cursor: "pointer",
            color: tab === t.key ? "#185FA5" : "#888",
            borderBottom: `2px solid ${tab === t.key ? "#185FA5" : "transparent"}`,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: "32px 16px", textAlign: "center", color: "#aaa" }}>Loading...</div>
      ) : (
        <>
          {/* ── THIS MONTH ── */}
          {tab === "month" && (
            <div>
              <div style={{ background: "#0B1C2E", padding: "20px 20px 24px", textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8ab4d4", marginBottom: 4 }}>
                  {fmtMonth(currentMonth)}
                </div>
                <div style={{ fontSize: 40, fontWeight: 700, color: "#fff" }}>{fmt(monthTotal)}</div>
                <div style={{ display: "flex", justifyContent: "center", gap: 28, marginTop: 12 }}>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#4CAF96" }}>{monthJobs}</div>
                    <div style={{ fontSize: 10, color: "#8ab4d4" }}>JOBS</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: "#4CAF96" }}>{monthHours.toFixed(1)}</div>
                    <div style={{ fontSize: 10, color: "#8ab4d4" }}>HOURS</div>
                  </div>
                </div>
              </div>

              {monthEarnings.length === 0 ? (
                <div className="g-empty">
                  <div className="g-empty-icon">💰</div>
                  <div className="g-empty-text">No earnings this month yet</div>
                </div>
              ) : (
                <div style={{ background: "#fff", marginTop: 12, borderRadius: 12, margin: "12px 12px 0", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                  {monthEarnings.map((e, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", padding: "14px", borderBottom: "1px solid #f8f8f8", gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>{e.jobNumber || `Job ${i + 1}`}</div>
                        <div style={{ fontSize: 11, color: "#888" }}>{e.brokerName || "—"}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        {/* Only show a dollar amount for paid jobs — pending
                            jobs render "—" so a guard never sees a phantom
                            number that isn't the courier owner's actual pay. */}
                        <div style={{ fontWeight: 700, fontSize: 14, color: e.status === "paid" ? "#111" : "#bbb" }}>
                          {e.status === "paid" ? fmt(e.amount) : "—"}
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          color: e.status === "paid" ? "#2d7a2d" : "#856404",
                          background: e.status === "paid" ? "#d4edda" : "#fff3cd",
                          padding: "2px 7px", borderRadius: 20,
                        }}>
                          {e.status === "paid" ? "Paid" : "Pending"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── YEAR TO DATE ── */}
          {tab === "ytd" && (
            <div style={{ padding: "12px 12px 0" }}>
              <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                <div style={{
                  display: "grid", gridTemplateColumns: "1fr 50px 50px 80px 60px",
                  padding: "8px 14px", background: "#f8f8f8", borderBottom: "1px solid #e8e8e8",
                  fontSize: 10, fontWeight: 700, color: "#888", gap: 4,
                }}>
                  <span>Month</span><span>Jobs</span><span>Hrs</span><span>Earned</span><span>Status</span>
                </div>
                {ytdRows.length === 0 ? (
                  <div style={{ padding: "20px 14px", fontSize: 12, color: "#bbb", textAlign: "center" }}>No data for {currentYear}</div>
                ) : ytdRows.map((row) => (
                  <div key={row.month} style={{
                    display: "grid", gridTemplateColumns: "1fr 50px 50px 80px 60px",
                    padding: "12px 14px", borderBottom: "1px solid #f8f8f8",
                    alignItems: "center", gap: 4, fontSize: 13,
                  }}>
                    <span style={{ fontWeight: 500 }}>{fmtMonth(row.month).replace(` ${currentYear}`, "")}</span>
                    <span style={{ color: "#555" }}>{row.jobs}</span>
                    <span style={{ color: "#555" }}>{row.hours.toFixed(1)}</span>
                    <span style={{ fontWeight: 700 }}>{fmt(row.earned)}</span>
                    <span style={{
                      fontSize: 10, fontWeight: 600,
                      color: row.paid ? "#2d7a2d" : "#856404",
                      background: row.paid ? "#d4edda" : "#fff3cd",
                      padding: "2px 7px", borderRadius: 20, textAlign: "center",
                    }}>
                      {row.paid ? "Paid" : "Pending"}
                    </span>
                  </div>
                ))}
                {ytdRows.length > 0 && (
                  <div style={{ display: "flex", padding: "12px 14px", background: "#f8f8f8", justifyContent: "space-between", fontSize: 13, fontWeight: 700 }}>
                    <span>YTD Total</span>
                    <span style={{ color: "#185FA5" }}>{fmt(ytdRows.reduce((s, r) => s + r.earned, 0))}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── BY YEAR ── */}
          {tab === "year" && (
            <div style={{ padding: "12px 12px 0", display: "flex", flexDirection: "column", gap: 12 }}>
              {[currentYear, priorYear].map((yr) => {
                const d = yearTotals[yr];
                return (
                  <div key={yr} style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
                    <div style={{ background: "#0B1C2E", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{yr}</span>
                      <span style={{ color: "#4CAF96", fontWeight: 700, fontSize: 18 }}>{fmt(d.earned)}</span>
                    </div>
                    <div style={{ display: "flex", borderBottom: "1px solid #f0f0f0" }}>
                      {[
                        { label: "JOBS", value: d.jobs },
                        { label: "HOURS", value: d.hours.toFixed(1) },
                      ].map((item) => (
                        <div key={item.label} style={{ flex: 1, padding: "12px", textAlign: "center" }}>
                          <div style={{ fontSize: 20, fontWeight: 700, color: "#111" }}>{item.value}</div>
                          <div style={{ fontSize: 10, color: "#888", fontWeight: 600 }}>{item.label}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: "12px 14px", fontSize: 11, color: "#888" }}>
                      Tax reference year · 1099 information in your profile
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
